import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Row,
  Space,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  CounsellingRequest,
  CounsellingRequestStatus,
} from "../../repository/data.default";
import {
  proposeCounsellingRequest,
  agreeCounsellingRequest,
  cancelCounsellingRequest,
  completeCounsellingRequest,
  rescheduleCounsellingRequest,
  updateCounsellingRequest,
  getPlatformUsers,
  getCollaborators,
  getCompanyCollaborators,
} from "../../repository/data.repository";
import { counsellingStatusColor } from "./CounsellingRequestsView.default";
import InModal from "../../controls/InModal";
import DynamicForm from "../../controls/DynamicForm";
import ManageStatusBanner from "../../controls/ManageStatusBanner";
import { useDialogAction } from "../../hooks/useDialogAction";
import {
  createScopedFilterRequest,
  createScopedSearchRequest,
  createSearchRequest,
} from "../../hooks/useSearchRequest";
import { fmtDateTime as fmt } from "../../utils/date";

export interface CounsellingManageDialogProps {
  readonly open?: boolean;
  readonly record?: CounsellingRequest;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
  // Deep-link to the linked calendar event (when the session is scheduled).
  readonly onViewEvent?: (eventUid: string) => void;
}

// State → one-line guidance: what the request is waiting on and the next move.
const guidanceFor = (status?: CounsellingRequestStatus): string => {
  switch (status) {
    case CounsellingRequestStatus.Requested:
      return "Awaiting a proposed slot — propose a time to move the request forward.";
    case CounsellingRequestStatus.Proposed:
      return "A slot is on the table — agree to schedule it, or counter-propose another time.";
    case CounsellingRequestStatus.Scheduled:
      return "The session is scheduled — mark it completed once it has taken place.";
    case CounsellingRequestStatus.Completed:
      return "This session has been completed.";
    case CounsellingRequestStatus.Cancelled:
      return "This request has been cancelled.";
    default:
      return "";
  }
};

/**
 * Single purpose-built surface for a counselling request once it exists. Replaces the old
 * "Edit" (crude field form) + "Manage" (lifecycle) split: details are editable here while the
 * request is open, and the status lifecycle (propose / agree / complete / cancel) lives in the
 * same view, gated by the current state.
 */
function CounsellingManageDialog(props: CounsellingManageDialogProps) {
  const [form] = Form.useForm();
  const { loading, setLoading, errorMessage, setErrorMessage, run } =
    useDialogAction({ onSuccess: props.onFinish });
  const [proposedAt, setProposedAt] = useState<Dayjs | null>(null);
  const [rescheduleAt, setRescheduleAt] = useState<Dayjs | null>(null);

  const status = props.record?.status;
  const isOpen =
    status !== CounsellingRequestStatus.Completed &&
    status !== CounsellingRequestStatus.Cancelled;
  const canPropose =
    status === CounsellingRequestStatus.Requested ||
    status === CounsellingRequestStatus.Proposed;
  const canAgree = status === CounsellingRequestStatus.Proposed;
  const canComplete = status === CounsellingRequestStatus.Scheduled;

  useEffect(() => {
    if (props.open) {
      setErrorMessage("");
      setLoading(false);
      setProposedAt(
        props.record?.proposedAt ? dayjs(props.record.proposedAt) : null,
      );
      setRescheduleAt(
        props.record?.scheduledAt ? dayjs(props.record.scheduledAt) : null,
      );
      form.setFieldsValue({
        therapist: props.record?.therapistUid
          ? {
              display: props.record.therapistUserName,
              value: props.record.therapistUid,
            }
          : undefined,
        patient: props.record?.patientUid
          ? {
              display: props.record.patientUserName,
              value: props.record.patientUid,
            }
          : undefined,
        reason: props.record?.reason,
      });
    } else {
      form.resetFields();
    }
  }, [props.open]);

  function onPropose(): void {
    if (!props.record || !proposedAt) return;
    run(() =>
      proposeCounsellingRequest(props.record!.uid, {
        proposedAt: proposedAt.toDate(),
      }),
    );
  }

  function onReschedule(): void {
    if (!props.record || !rescheduleAt) return;
    run(() =>
      rescheduleCounsellingRequest(props.record!.uid, {
        proposedAt: rescheduleAt.toDate(),
      }),
    );
  }

  function onSaveDetails(): void {
    if (!props.record) return;
    const { therapist, patient, reason } = form.getFieldsValue();
    run(() =>
      updateCounsellingRequest({
        uid: props.record!.uid,
        rowVersion: props.record!.rowVersion,
        therapistUid: therapist?.value,
        patientUid: patient?.value,
        reason,
      }),
    );
  }

  // Methods

  // The request is already tied to a company — scope both pickers to it so therapist/patient edits
  // stay valid (a therapist must be assigned to the request's company; the patient belongs to it).
  const requestCompanyUid = (props.record as any)?.companyUid as string | undefined;

  // Patients: searched platform-wide, narrowed to the request's company when it carries one.
  const platformUsersRequest = createScopedFilterRequest(
    getPlatformUsers,
    "userName",
    () => [["companyUid", requestCompanyUid]],
    undefined,
    { subDisplayField: "companyName" },
  );

  // Therapists: the request's company's assigned affiliates when scoped, all collaborators otherwise.
  const companyCollaboratorsRequest = createScopedSearchRequest(
    getCompanyCollaborators,
    "userUserName",
    "companyUid",
    () => requestCompanyUid,
    undefined,
    { valueField: "userUid", subDisplayField: "companyName" },
  );
  const allCollaboratorsRequest = createSearchRequest(
    getCollaborators,
    "userName",
    undefined,
    { subDisplayField: "companyName" },
  );
  const collaboratorsRequest = (search: string) =>
    requestCompanyUid
      ? companyCollaboratorsRequest(search)
      : allCollaboratorsRequest(search);

  return (
    <InModal
      open={props.open}
      size="large"
      title="Manage Counselling Request"
      onCancel={props.onCancel}
      actions={
        <Space>
          <Button disabled={loading} onClick={props.onCancel}>
            Close
          </Button>
          {props.record?.eventUid && props.onViewEvent && (
            <Button onClick={() => props.onViewEvent!(props.record!.eventUid!)}>
              View on calendar
            </Button>
          )}
          {isOpen && (
            <Button loading={loading} onClick={onSaveDetails}>
              Save Details
            </Button>
          )}
          {canAgree && (
            <Button
              type="primary"
              loading={loading}
              onClick={() => run(() => agreeCounsellingRequest(props.record!.uid))}
            >
              Agree &amp; Schedule
            </Button>
          )}
          {canComplete && (
            <Button
              type="primary"
              loading={loading}
              onClick={() =>
                run(() => completeCounsellingRequest(props.record!.uid))
              }
            >
              Mark Completed
            </Button>
          )}
          {isOpen && (
            <Button
              danger
              loading={loading}
              onClick={() => run(() => cancelCounsellingRequest(props.record!.uid))}
            >
              Cancel Request
            </Button>
          )}
        </Space>
      }
    >
      {/* Status banner + contextual guidance */}
      <ManageStatusBanner
        tagColor={counsellingStatusColor(status)}
        status={status}
        guidance={guidanceFor(status)}
      />

      <Row gutter={16} className="flex flex-row">
        {/* Details — editable while the request is open, read-only once closed */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            Details
          </Typography.Text>
          {isOpen ? (
            <DynamicForm
              form={form}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "combobox",
                        name: "therapist",
                        label: "Therapist",
                        request: collaboratorsRequest,
                      },
                      {
                        type: "combobox",
                        name: "patient",
                        label: "Patient (subject)",
                        request: platformUsersRequest,
                      },
                      {
                        type: "areabox",
                        name: "reason",
                        label: "Reason",
                      },
                    ],
                  },
                ],
              }}
            />
          ) : (
            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                {
                  key: "therapist",
                  label: "Therapist",
                  children: props.record?.therapistUserName ?? "—",
                },
                {
                  key: "patient",
                  label: "Patient",
                  children: props.record?.patientUserName ?? "—",
                },
                {
                  key: "reason",
                  label: "Reason",
                  children: props.record?.reason || "—",
                },
              ]}
            />
          )}
        </Col>

        {/* Scheduling — read-only state + propose / counter-propose control */}
        <Col flex="1 1 0" className="min-w-0">
          <Typography.Text strong className="mb-2 block">
            Scheduling
          </Typography.Text>
          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              {
                key: "requestedBy",
                label: "Requested By",
                children: props.record?.requestedByUserName ?? "—",
              },
              {
                key: "proposedAt",
                label: "Proposed Slot",
                children: fmt(props.record?.proposedAt),
              },
              {
                key: "scheduledAt",
                label: "Scheduled At",
                children: fmt(props.record?.scheduledAt),
              },
            ]}
          />

          {canPropose && (
            <div className="mt-4">
              <Typography.Text className="mb-1 block font-medium">
                {status === CounsellingRequestStatus.Proposed
                  ? "Counter-propose a slot"
                  : "Propose a slot"}
              </Typography.Text>
              <Space.Compact className="w-full">
                <DatePicker
                  className="flex-auto"
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  value={proposedAt}
                  onChange={(value) => setProposedAt(value)}
                />
                <Button
                  type="primary"
                  loading={loading}
                  disabled={!proposedAt}
                  onClick={onPropose}
                >
                  {status === CounsellingRequestStatus.Proposed
                    ? "Counter-propose"
                    : "Propose"}
                </Button>
              </Space.Compact>
              <Typography.Text type="secondary" className="mt-1 block text-xs">
                The other party agrees to the current slot to schedule the session.
              </Typography.Text>
            </div>
          )}

          {canComplete && (
            <div className="mt-4">
              <Typography.Text className="mb-1 block font-medium">
                Reschedule
              </Typography.Text>
              <Space.Compact className="w-full">
                <DatePicker
                  className="flex-auto"
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  value={rescheduleAt}
                  onChange={(value) => setRescheduleAt(value)}
                />
                <Button
                  type="primary"
                  loading={loading}
                  disabled={!rescheduleAt}
                  onClick={onReschedule}
                >
                  Reschedule
                </Button>
              </Space.Compact>
              <Typography.Text type="secondary" className="mt-1 block text-xs">
                Moves the confirmed session to a new time and updates the calendar.
              </Typography.Text>
            </div>
          )}
        </Col>
      </Row>

      {errorMessage && (
        <Alert className="mt-3" message={errorMessage} type="error" />
      )}
    </InModal>
  );
}

export default CounsellingManageDialog;
